import React from 'react';
import { Box, BoxProps, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CaseActivityChangesType } from '@components/CaseActivityDrawer/types/CaseActivityRowType';
import {
	formatActivityChangeDetails,
	FormatActivityChangeDetailsReturn, formatDocumentChangeDetails, getFieldName
} from '@components/CaseActivityDrawer/utilities/formatActivityChangeDetails';
import { useSelector } from 'react-redux';
import { InsuranceAuthStatusTypesSelector } from '@store/selectors/InsuranceAuthStatusTypesSelector';
import { CaseActivityTypeEnum } from '@interfaces/CaseActivityTypeEnum';
import classNames from 'classnames';
import {
	changedFieldNamesToDisplay,
	documentActions
} from '@components/CaseActivityDrawer/utilities/CaseActivityConstants';

interface CaseActivityChangesProps extends BoxProps {
	changes: CaseActivityChangesType;
	formattedTime: string;
	activityType: CaseActivityTypeEnum;
}

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(0.5, 1),
		width: 'full',
		fontSize: 12,
		fontFamily: 'HCo Whitney SSm',
		backgroundColor: theme.palette.primary[50],
		borderRadius: 4,
		marginBottom: theme.spacing(2),
	},
	horizontalRule: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	changesDiv: {
		display: 'flex',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	changedItemsDiv: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		flex: 3
	},
	documentChangedItemsDiv: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	removedItemText: {
		textDecoration: 'line-through',
	},
	addedItemText: {
		flex: '0 0 70%',
		wordBreak: 'break-word',
		fontWeight: 'bold',
	},
	oldItemText: {
		textDecoration: 'line-through',
		textDecorationColor: theme.palette.grey[600],
		color: theme.palette.grey[700],
	},
	changeTitle: {
		flex: 1
	},
	changeFieldText: {
		flex: '0 0 25%',
	},
	changesItemTitle: {
		fontSize: '14px',
		color: theme.palette.grey[700],
	}
}));


const FieldChangeDisplay = ({ changedField, changeDetails }: FormatActivityChangeDetailsReturn) => {
	const classes = useStyles();

	return (
		<>
			<Box className={classes.changeFieldText}>
				<strong>{changedField}:</strong>
			</Box>
			<Box className={classes.changedItemsDiv}>
				<Box>
					{changeDetails?.currValue ?? '--'}
				</Box>
				<Box className={classes.oldItemText}>
					{changeDetails.prevValue}
				</Box>
			</Box>
		</>
	);
};

const DocumentFieldChangeDisplay = ({ changedField, changeDetails, formattedTime }: FormatActivityChangeDetailsReturn) => {
	const classes = useStyles();
	const isDeleted = changedField === 'Deleted';
	return (
		<Box className={classes.changedItemsDiv} key={changedField}>
			<Box>
				{changedField}
			</Box>
			<Box className={classes.documentChangedItemsDiv}>
				<Box className={classNames(classes.addedItemText, { [classes.removedItemText]: isDeleted })}>
					{changeDetails.currValue}
				</Box>
				<Box>
					{formattedTime}
				</Box>
			</Box>
		</Box>
	);
}

export function CaseActivityChanges({ changes, formattedTime, activityType, ...props }: CaseActivityChangesProps) {
	const classes = useStyles();
	const authStatusConfigTypes = useSelector(InsuranceAuthStatusTypesSelector);
	const { addNodes = [], replacedNodes = [], removeNode = [] } = changes;
	const changesToDisplay = [...replacedNodes, ...removeNode]?.filter((change) =>
		changedFieldNamesToDisplay.includes(getFieldName(change)[0]),
	);

	const equipmentChanges = changesToDisplay?.filter((change) => {
		return getFieldName(change)[0] === 'Equipment';
	});

	const allOtherChanges = changesToDisplay?.filter((change) => {
		return getFieldName(change)[0] !== 'Equipment';
	});

	const authorizationStatusChanges = allOtherChanges?.[0]?.fieldFriendlyName === 'Authorization';

	const documentChanges = [...addNodes, ...removeNode].filter((change) => {
		return documentActions.includes(change.fieldFriendlyName);
	});

	const allOtherChangesTitle = authorizationStatusChanges ? 'Authorization status update.' :
		activityType === CaseActivityTypeEnum.RESCHEDULED ?
			'This case was rescheduled.' :
			'This request was edited.';

	return (
		<>
			{/* Show all changes except equipment in same box */}
			{/* Authorization changes show as separate case activity entry from all other changes */}
			{allOtherChanges?.length > 0 && (
				<>
					<Box {...props} className={classes.root}>
						<Typography className={classes.changesItemTitle}>
							{allOtherChangesTitle}
						</Typography>
						<Divider className={classes.horizontalRule} />
						<Box pb={3}>
							{allOtherChanges.map((change) => {
								const { changedField, changeDetails } = formatActivityChangeDetails(change, authStatusConfigTypes);
								return (
									<Box className={classes.changesDiv} key={changedField}>
										<FieldChangeDisplay changedField={changedField} changeDetails={changeDetails} />
									</Box>
								);
							})}
						</Box>
					</Box>
				</>
			)}
			{/* Show separate blue box for each procedure's equipment changes */}
			{equipmentChanges?.length > 0 && (
				<>
					{equipmentChanges.map((change) => {
						const { changedField, changeDetails } = formatActivityChangeDetails(change, authStatusConfigTypes);
						return (
							<Box {...props} className={classes.root} key={changeDetails.changeTitle}>
								<>
									<Box>{changeDetails.changeTitle}</Box>
									<Divider className={classes.horizontalRule} />
									<Box className={classes.changesDiv}>
										<FieldChangeDisplay changedField={changedField} changeDetails={changeDetails} />
									</Box>
								</>
							</Box>
						)
					})}
				</>
			)}
			{/* Show separate blue box for procedure's document changes */}
			{documentChanges?.length > 0 && (
				<Box {...props} className={classes.root}>
					<Typography className={classes.changesItemTitle}>
							Documents changed.
					</Typography>
					<Divider className={classes.horizontalRule} />
					<Box pb={3}>
						{documentChanges.map((change) => {
							const { changedField, changeDetails } = formatDocumentChangeDetails(change);
							return (
								<Box className={classes.changesDiv} key={changedField}>
									<DocumentFieldChangeDisplay changedField={changedField} changeDetails={changeDetails} formattedTime={formattedTime} />
								</Box>
							);
						})}
					</Box>
				</Box>
			)}
		</>
	);
}
